import { HiLink } from "react-icons/hi";
import { RiShareForwardFill, RiTwitterXLine } from "react-icons/ri";
import { FaFacebookF, FaRedditAlien, FaWhatsapp } from "react-icons/fa";
import { Box } from "rebass";
import styled from "styled-components";
import { Text } from "rebass/styled-components";
import { Button } from "..";
import { TClipShareProps } from "../@types";
import {
  trackClipSocialShare,
  trackCreatorCardShare,
} from "../../../Analytics";
import { Tooltip } from "../../Tooltip";
import { Subheader } from "../../Text";
import theme from "../../../../theme/themeThree";
import {
  addAttributionParams,
  getAttributionUserId,
} from "MVVM/Utilities/Attribution";
import { useUser } from "MVVM/Hooks/useUser";
import { useTrackCampaignAction } from "MVVM/Hooks/useTrackCampaignAction";

const StyledShare = styled(RiShareForwardFill)`
  position: relative;
`;

const DropdownContainer = styled(Tooltip)`
  display: flex;
  min-width: 38px;
  z-index: 1;
  &:hover {
    cursor: default;
  }
`;

export function CopyLink({
  classification,
  clipLink,
  clip,
  card,
  children,
  hideOnMobile,
  isMontage,
}: any) {
  const { allstarUser } = useUser();
  const { trackCampaignAction } = useTrackCampaignAction();
  const userId = getAttributionUserId(allstarUser);
  const linkWithAttribution = addAttributionParams(clipLink, userId);
  const EncodedURI = encodeURI(linkWithAttribution);
  classification = classification ? classification : "secondaryRounded";

  return (
    <>
      <DropdownContainer
        position={{
          top: "-42px",
          padding: "10px",
          right: "-30px",
        }}
        type="click"
        timeout={true}
        theme="helpTooltip"
        align="right"
        tooltipContent={
          <Box name="url-copied-tooltip">URL copied to clipboard!</Box>
        }
      >
        <CopyLinkButton
          title="Copy Link"
          display="flex"
          size="social"
          width="100%"
          hideOnMobile={hideOnMobile}
          name="clip-options-copy-link"
          classification="secondaryRounded"
          justifyContent="center"
          alignContent="center"
          alignItems="center"
          onClick={() => {
            if (clip) {
              trackClipSocialShare({
                platform: "Clipboard",
                isMontage: isMontage,
                clip,
              });
              trackCampaignAction("CLIP_SHARED");
            } else if (card) {
              trackCreatorCardShare({ ...card });
            }
            navigator.clipboard.writeText(EncodedURI);
          }}
        >
          <StyledCopyLink size={20} />
          {children}
        </CopyLinkButton>
      </DropdownContainer>
    </>
  );
}

const CopyLinkButton = styled(Button)`
  align-items: center;
  background-color: ${({ theme }) => theme.colors.envy};
  display: flex;

  :hover {
    background-color: ${({ theme }) => theme.colors.darkerEnvy};
  }
`;

const StyledCopyLink = styled(HiLink)`
  position: relative;
`;

export function ShareButton(props: any) {
  return <Button {...props}>{props.children}</Button>;
}

export function ClipShare({
  size,
  children,
  classification,
  mr,
  clip,
  onClick,
  hideOnDesktop,
  ...props
}: any) {
  classification = classification ? classification : "secondaryRounded";

  return (
    <>
      <Button
        width="100%"
        hideOnDesktop={hideOnDesktop}
        display="flex"
        size="social"
        classification="secondaryRounded"
        justifyContent="center"
        alignContent="center"
        alignItems="center"
        style={{
          backgroundColor: theme.colors.envy,
        }}
        onClick={() => {
          onClick();
        }}
        {...props}
      >
        <StyledShare size={20} />

        {children}
      </Button>
    </>
  );
}

export function RedditShare({
  link,
  clip,
  card,
  title,
  children,
}: TClipShareProps) {
  const { allstarUser } = useUser();
  const { trackCampaignAction } = useTrackCampaignAction();
  const userId = getAttributionUserId(allstarUser);
  const linkWithAttribution = addAttributionParams(link, userId);

  return (
    <>
      <ShareButton
        title="Share to Reddit"
        name="share-modal-reddit"
        justifyContent={"center"}
        alignItems="center"
        flex={true}
        display="flex"
        href={`https://www.reddit.com/submit?url=${encodeURIComponent(
          linkWithAttribution,
        )}${title ? `&title=${title}` : ``}`}
        target="_blank"
        classification="reddit"
        onClick={() => {
          if (clip) {
            trackClipSocialShare({
              platform: "Reddit",
              clip,
            });
            trackCampaignAction("CLIP_SHARED");
          } else if (card) {
            trackCreatorCardShare(card);
          }
        }}
      >
        <FaRedditAlien size={24} color={"#f8fcfd"}></FaRedditAlien>
        {children}
      </ShareButton>
    </>
  );
}

export function TwitterShare({
  link,
  clip,
  card,
  title,
  children,
}: TClipShareProps) {
  const { allstarUser } = useUser();
  const { trackCampaignAction } = useTrackCampaignAction();
  const userId = getAttributionUserId(allstarUser);
  const linkWithAttribution = addAttributionParams(link, userId);

  return (
    <>
      <ShareButton
        title="Share to Twitter"
        name="share-modal-twitter"
        justifyContent={"center"}
        alignItems="center"
        flex={true}
        display="flex"
        href={`https://twitter.com/intent/tweet?url=${linkWithAttribution}&text=${title}`}
        target="_blank"
        classification="twitter"
        onClick={() => {
          if (clip) {
            trackClipSocialShare({
              platform: "Twitter",
              clip,
            });
            trackCampaignAction("CLIP_SHARED");
          } else if (card) {
            trackCreatorCardShare(card);
          }
        }}
      >
        <RiTwitterXLine size={24} color={"#f8fcfd"}></RiTwitterXLine>

        {children}
      </ShareButton>
    </>
  );
}

export function FacebookShare({
  link,
  clip,
  card,

  children,
}: TClipShareProps) {
  const { allstarUser } = useUser();
  const { trackCampaignAction } = useTrackCampaignAction();
  const userId = getAttributionUserId(allstarUser);
  const linkWithAttribution = addAttributionParams(link, userId);
  const EncodedURI = encodeURI(linkWithAttribution);

  return (
    <>
      <ShareButton
        title="Share to Facebook"
        name="share-modal-facebook"
        justifyContent={"center"}
        alignItems="center"
        flex={true}
        display="flex"
        href={`https://www.facebook.com/dialog/share?app_id=595195531196882&display=popup&href=${EncodedURI}&redirect_uri=${EncodedURI}`}
        target="_blank"
        classification="facebook"
        onClick={() => {
          if (clip) {
            trackClipSocialShare({
              platform: "Facebook",
              clip,
            });
            trackCampaignAction("CLIP_SHARED");
          } else if (card) {
            trackCreatorCardShare(card);
          }
        }}
      >
        <FaFacebookF size={24} color={"#f8fcfd"}></FaFacebookF>

        {children}
      </ShareButton>
    </>
  );
}

export function ClipboardShare({ link, clip }: TClipShareProps) {
  const { allstarUser } = useUser();
  const { trackCampaignAction } = useTrackCampaignAction();
  const userId = getAttributionUserId(allstarUser);
  const linkWithAttribution = addAttributionParams(link, userId);
  const EncodedURI = encodeURI(linkWithAttribution);

  return (
    <>
      <Tooltip
        position={{
          top: "-42px",
          padding: "10px",
          right: "-30px",
        }}
        type="click"
        timeout={true}
        theme="helpTooltip"
        align="right"
        name="share-modal-clipboard"
        tooltipContent="URL copied to clipboard!"
      >
        <ShareButton
          flex={false}
          width="100%"
          size="publish"
          justifyContent="space-between"
          classification="modalShare"
          alignItems="center"
          onClick={() => {
            trackClipSocialShare({
              platform: "Clipboard",
              clip,
            });
            trackCampaignAction("CLIP_SHARED");

            navigator.clipboard.writeText(EncodedURI);
          }}
        >
          <UriString>{EncodedURI}</UriString>
          <CopyButton>COPY LINK</CopyButton>
        </ShareButton>
      </Tooltip>
    </>
  );
}
export function WhatsAppShare({ link, clip, card, children }: TClipShareProps) {
  const { allstarUser } = useUser();
  const { trackCampaignAction } = useTrackCampaignAction();
  const userId = getAttributionUserId(allstarUser);
  const linkWithAttribution = addAttributionParams(link, userId);

  return (
    <>
      <ShareButton
        title="Share to WhatsApp"
        name="share-modal-whatsapp"
        justifyContent={"center"}
        alignItems="center"
        flex={true}
        display="flex"
        href={`https://api.whatsapp.com/send/?phone&text=${linkWithAttribution}`}
        target="_blank"
        classification="whatsApp"
        onClick={() => {
          if (clip) {
            trackClipSocialShare({
              platform: "WhatsApp",
              clip,
            });
            trackCampaignAction("CLIP_SHARED");
          } else if (card) {
            trackCreatorCardShare(card);
          }
        }}
      >
        <FaWhatsapp size={24} color={"#f8fcfd"}></FaWhatsapp>

        {children}
      </ShareButton>
    </>
  );
}
const UriString = styled(Text)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${(props) => props.theme.colors.chalk};
`;

const CopyButton = styled(Subheader)`
  border-radius: 4px;
  padding: 4px;
`;
